import React from "react";
import {
  Stack,
  Text,
  Icon,
  Link,
  Separator,
} from "@fluentui/react";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import { InboundWebhookLogResponse, InboundWebhookTagResponse, InboundWebhookTagTypeEnum } from "@/api/webhookLogs";
import { InboundWebhookProvider } from "@/types/webhooks";

interface WebhookTagsPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  log: InboundWebhookLogResponse | null;
  providers: InboundWebhookProvider[];
}

const WebhookTagsPanel: React.FC<WebhookTagsPanelProps> = ({
  isOpen,
  onDismiss,
  log,
  providers,
}) => {
  const getTagIcon = (type: InboundWebhookTagTypeEnum) => {
    switch (type) {
      case InboundWebhookTagTypeEnum.File:
        return "Attach";
      case InboundWebhookTagTypeEnum.Text:
      default:
        return "Tag";
    }
  };

  const renderTagValue = (tag: InboundWebhookTagResponse) => {
    if (tag.Type === InboundWebhookTagTypeEnum.File) {
      // For file types, display as a clickable link if it's a valid URL, otherwise as text
      try {
        new URL(tag.Value);
        return (
          <Stack tokens={{ childrenGap: 8 }}>
            <Link
              href={tag.Value}
              target="_blank"
              rel="noopener noreferrer"
              styles={{
                root: {
                  fontSize: "14px",
                  wordBreak: "break-all",
                  color: "#0078d4",
                  textDecoration: "underline",
                },
              }}
            >
              {tag.Value}
            </Link>
            <Link
              href={`/files/${encodeURIComponent(tag.Value)}`}
              target="_blank"
              rel="noopener noreferrer"
              styles={{
                root: {
                  fontSize: "12px",
                  color: "#0078d4",
                  textDecoration: "underline",
                  fontStyle: "italic",
                },
              }}
            >
              → View file details
            </Link>
          </Stack>
        );
      } catch {
        // If not a valid URL, treat as internal file ID and show file details link
        return (
          <Stack tokens={{ childrenGap: 8 }}>
            <Text
              variant="medium"
              styles={{
                root: {
                  fontSize: "14px",
                  wordBreak: "break-all",
                  color: "#323130",
                  fontFamily: "monospace",
                },
              }}
            >
              {tag.Value}
            </Text>
            <Link
              href={`/files/${encodeURIComponent(tag.Value)}`}
              target="_blank"
              rel="noopener noreferrer"
              styles={{
                root: {
                  fontSize: "12px",
                  color: "#0078d4",
                  textDecoration: "underline",
                  fontStyle: "italic",
                },
              }}
            >
              → View file details
            </Link>
          </Stack>
        );
      }
    }

    return (
      <Text
        variant="medium"
        styles={{
          root: {
            fontSize: "14px",
            wordBreak: "break-all",
            color: "#323130",
            fontFamily: "monospace",
          },
        }}
      >
        {tag.Value}
      </Text>
    );
  };

  // Get derived values from log prop with null safety
  const tags = log?.Tags || [];
  const logId = log?.Id || 0;
  const logDate = log?.DateUtc || "N/A";
  
  // Get provider name by ID
  const getProviderName = (providerId: number) => {
    const provider = providers.find((p) => p.Id === providerId);
    return provider ? provider.Description : `Provider ${providerId}`;
  };
  
  const providerName = log ? getProviderName(log.InboundWebhookProviderId) : "Unknown Provider";

  // If the panel is closed or log is null, return a minimal component
  if (!isOpen || !log) {
    return (
      <RigthPanel
        header="Webhook Tags"
        isOpen={false}
        type={RigthPanelType.medium}
        dismissPanel={onDismiss}
        childComponent={() => <div />}
        childProps={{}}
      />
    );
  }

  return (
    <RigthPanel
      header={`Webhook Tags (${tags.length})`}
      isOpen={isOpen}
      type={RigthPanelType.medium}
      dismissPanel={onDismiss}
      childComponent={() => (
        <Stack
          styles={{
            root: {
              height: "100%",
              padding: "16px",
              overflowY: "auto",
            },
          }}
        >
          {/* Log Info Header */}
          <Stack
            styles={{
              root: {
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                marginBottom: "16px",
              },
            }}
          >
            <Text
              variant="mediumPlus"
              styles={{
                root: {
                  fontWeight: "600",
                  color: "#323130",
                  marginBottom: "8px",
                },
              }}
            >
              Log #{logId}
            </Text>
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                <Icon
                  iconName="DateTime"
                  styles={{
                    root: {
                      fontSize: "12px",
                      color: "#605e5c",
                    },
                  }}
                />
                <Text
                  variant="small"
                  styles={{
                    root: {
                      color: "#605e5c",
                      fontFamily: "monospace",
                    },
                  }}
                >
                  {logDate}
                </Text>
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                <Icon
                  iconName="WebComponents"
                  styles={{
                    root: {
                      fontSize: "12px",
                      color: "#605e5c",
                    },
                  }}
                />
                <Text
                  variant="small"
                  styles={{
                    root: {
                      color: "#605e5c",
                      fontWeight: "600",
                    },
                  }}
                >
                  {providerName}
                </Text>
              </Stack>
            </Stack>
          </Stack>

          <Separator />

          {/* Tags List */}
          {tags.length === 0 ? (
            <Stack
              horizontalAlign="center"
              verticalAlign="center"
              styles={{ root: { height: "200px" } }}
            >
              <Stack tokens={{ childrenGap: 12 }} horizontalAlign="center">
                <Icon
                  iconName="Tag"
                  styles={{
                    root: {
                      fontSize: "48px",
                      color: "#a19f9d",
                    },
                  }}
                />
                <Text
                  variant="medium"
                  styles={{
                    root: {
                      color: "#605e5c",
                    },
                  }}
                >
                  No tags associated with this log
                </Text>
              </Stack>
            </Stack>
          ) : (
            <Stack tokens={{ childrenGap: 16 }}>
              <Text
                variant="medium"
                styles={{
                  root: {
                    fontWeight: "600",
                    color: "#323130",
                    marginBottom: "8px",
                  },
                }}
              >
                Tags ({tags.length})
              </Text>
              
              {tags.map((tag) => (
                <Stack
                  key={tag.Id}
                  styles={{
                    root: {
                      padding: "12px 16px",
                      border: "1px solid #e1e1e1",
                      borderRadius: "6px",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { marginBottom: "8px" } }}>
                    <Icon
                      iconName={getTagIcon(tag.Type)}
                      styles={{
                        root: {
                          fontSize: "14px",
                          color: tag.Type === InboundWebhookTagTypeEnum.File ? "#0078d4" : "#605e5c",
                        },
                      }}
                    />
                    <Text
                      variant="medium"
                      styles={{
                        root: {
                          fontWeight: "600",
                          color: "#323130",
                        },
                      }}
                    >
                      {tag.Key}
                    </Text>
                    <Text
                      variant="small"
                      styles={{
                        root: {
                          color: "#a19f9d",
                          fontStyle: "italic",
                          textTransform: "capitalize",
                        },
                      }}
                    >
                      ({tag.Type === InboundWebhookTagTypeEnum.File ? "File" : "Text"})
                    </Text>
                  </Stack>
                  <Stack styles={{ root: { marginLeft: "22px" } }}>
                    {renderTagValue(tag)}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
      childProps={{}}
    />
  );
};

export default WebhookTagsPanel;
